import React, { useState } from 'react';
import { Pressable, ScrollView } from 'react-native';

import { BrandColor } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';
import Button from '../../ui/Button';
import { ArrowsChevronRightIcon, GiftBoxIcon } from '../../ui/Icons';
import {
  BodyText,
  PortalOverlay,
  PortalOverlayModal,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
} from '../../ui';

import './Sidebar.module.scss';
import { ABTestMenuModal } from '../developer/ABTestMenu';

import type { State as CurrentUserState } from '../../../store/CurrentUser.types';
import SidebarClientSection from './SidebarClientSection';
import SidebarProSection from './SidebarProSection';
import SidebarStyleSeatSection from './SidebarStyeSeatSection';
import { InstagramCircleIcon } from '../../ui/Icons/InstagramCircleIcon';
import { XLogoIcon } from '../../ui/Icons/XLogoIcon';
import { FacebookCircleIcon } from '../../ui/Icons/FacebookCircleIcon';
import TrackedOption from './TrackedOption';
import { generatePlatformMessage } from '../../../modules/referral/helpers';
import { ShareTrigger } from '../../consumer/sharing';
import { ShareTarget } from '../../../modules/share';
import { useReferralEntrypointProps } from '../../consumer/referrals/useReferralEntrypointProps';

interface SidebarProps {
  isLoggedIn: boolean;
  isProvider: boolean;
  utmParams: {};
  user: CurrentUserState;
  linkTo: (url: string, params?: {}) => Promise<any>;
  onClose: () => void;
  onLogin: () => void;
  onSocialClick: (e: any, channel: string) => void;
  openPrivacy: () => void;
  openTerms: () => void;
  openSearchModal: () => void;
  switchToBusiness: () => void;
  onSignUp: () => void;
  goToExternalPage: (route: string, target?: string) => void;
  openHelpCenter: () => void;
  goToHome: () => void;
  goToMyAccount: () => void;
  goToMyAppointments: () => void;
  displayDeveloperMenu: () => void;
  showProOnboardingEntryPoints: boolean;
}

/**
 * Displays the mobile sidebar
 */
const Sidebar: React.FCWithChildren<SidebarProps> = ({
  isLoggedIn,
  isProvider,
  utmParams,
  user,
  linkTo,
  onClose,
  onLogin,
  onSocialClick,
  openPrivacy,
  openTerms,
  openSearchModal,
  switchToBusiness,
  onSignUp,
  goToExternalPage,
  openHelpCenter,
  goToHome,
  goToMyAccount,
  goToMyAppointments,
  displayDeveloperMenu,
  showProOnboardingEntryPoints,
}) => {
  const [isDeveloperMenuOpen, setDeveloperMenuOpen] = useState(false);

  const referralEntrypointProps = useReferralEntrypointProps({
    target: ShareTarget.PLATFORM,
    location: 'client_promotions_page',
    message: generatePlatformMessage,
  });

  return (
    <PortalOverlay
      name="sidebar"
      animationStyle="slideRight"
      overflowScroll
    >
      <PortalOverlayModal position="sidebar">
        <PortalOverlayHeader
          alignItems="center"
          renderRightAction={() => (
            <PortalOverlayHeaderCloseBtn onCloseRequest={() => onClose()} />
          )}
        />
        <ScrollView showsVerticalScrollIndicator={false}>
          <Box>
            {!isLoggedIn && (
              <Box
                paddingLeft={16}
                width={110}
                paddingTop={20}
                minHeight={40}
              >
                <TrackedOption
                  name="sidebar-login-clicked"
                  testID="sidebar-login-option"
                >
                  <Button size="medium" appearance="primary" onClick={onLogin}>
                    Log In
                  </Button>
                </TrackedOption>
              </Box>
            )}
          </Box>
          <Box paddingX={16} paddingTop={20}>
            <ShareTrigger
              {...referralEntrypointProps}
              renderTrigger={onClick => (
                <TrackedOption
                  name="sidebar-50-for-you-50-for-a-friend-clicked"
                  testID="sidebar-50-for-you-50-for-a-friend-option"
                  onPress={isLoggedIn ? onClick : onLogin}
                >
                  <Box
                    style={{ backgroundColor: BrandColor.LightGreen, borderRadius: 4 }}
                    minHeight={52}
                  >
                    <Inline
                      space={10}
                      paddingLeft={12}
                      alignItems="center"
                      width="100%"
                      style={{
                        margin: 'auto',
                      }}
                    >
                      <GiftBoxIcon width={20} height={20} />
                      <Box width="80%">
                        <BodyText weight="bold">
                          $50 for you, $50 for a friend!
                        </BodyText>
                      </Box>
                      <ArrowsChevronRightIcon />
                    </Inline>
                  </Box>
                </TrackedOption>
              )}
            />
          </Box>
          {isLoggedIn && (
            <Inline paddingTop={20} paddingLeft={20}>
              <BodyText appearance="larger" weight="bold">
                {`👋 Hi, ${user.first_name}`}
              </BodyText>
            </Inline>
          )}
          {!isLoggedIn && (
            <Box>
              {showProOnboardingEntryPoints && (
                <SidebarProSection
                  isProvider={isProvider}
                  linkTo={linkTo}
                  goToExternalPage={goToExternalPage}
                />
              )}
              <SidebarClientSection
                isLoggedIn={isLoggedIn}
                isProvider={isProvider}
                utmParams={utmParams}
                referralEntrypointProps={referralEntrypointProps}
                linkTo={linkTo}
                goToSearch={openSearchModal}
                onLogin={onLogin}
                switchToBusiness={switchToBusiness}
                onSignUp={onSignUp}
                goToHome={goToHome}
                goToMyAccount={goToMyAccount}
                goToMyAppointments={goToMyAppointments}
              />
              <SidebarStyleSeatSection
                showProOnboardingEntryPoints={showProOnboardingEntryPoints}
                goToExternalPage={goToExternalPage}
                openTerms={openTerms}
                openPrivacy={openPrivacy}
                openHelpCenter={openHelpCenter}
                displayDeveloperMenu={displayDeveloperMenu}
              />
            </Box>
          )}

          {isLoggedIn && (
            <Box>
              <SidebarClientSection
                isLoggedIn={isLoggedIn}
                isProvider={isProvider}
                utmParams={utmParams}
                referralEntrypointProps={referralEntrypointProps}
                linkTo={linkTo}
                goToSearch={openSearchModal}
                onLogin={onLogin}
                switchToBusiness={switchToBusiness}
                onSignUp={onSignUp}
                goToHome={goToHome}
                goToMyAccount={goToMyAccount}
                goToMyAppointments={goToMyAppointments}
              />
              {(
                showProOnboardingEntryPoints && (
                  <SidebarProSection
                    isProvider={isProvider}
                    linkTo={linkTo}
                    goToExternalPage={goToExternalPage}
                  />
                )
              )}

              <SidebarStyleSeatSection
                showProOnboardingEntryPoints={showProOnboardingEntryPoints}
                goToExternalPage={goToExternalPage}
                openTerms={openTerms}
                openPrivacy={openPrivacy}
                openHelpCenter={openHelpCenter}
                displayDeveloperMenu={displayDeveloperMenu}
              />
            </Box>
          )}

          <Box padding={20}>
            <BodyText appearance="medium" weight="bold">
              Follow us
            </BodyText>
            <Inline space={16} paddingTop={16}>
              <Pressable onPress={e => onSocialClick(e, 'facebook')}>
                <FacebookCircleIcon width={42} height={42} />
              </Pressable>
              <Pressable onPress={e => onSocialClick(e, 'instagram')}>
                <InstagramCircleIcon width={42} height={42} />
              </Pressable>
              <Pressable onPress={e => onSocialClick(e, 'twitter')}>
                <XLogoIcon width={42} height={42} />
              </Pressable>
            </Inline>
          </Box>
        </ScrollView>
      </PortalOverlayModal>
      {isDeveloperMenuOpen && (
        <ABTestMenuModal
          onClose={() => setDeveloperMenuOpen(false)}
        />
      )}
    </PortalOverlay>
  );
};

export default Sidebar;
